import styled from "styled-components";
import { UniqueColors } from "./UniqueColors";
import { DesignTokens } from "../../common-ui/styles/design-tokens";
import { Row } from "../../common-ui/components/grid/Row";
import { Col } from "../../common-ui/components/grid/Col";
import { LabeledItem } from "../../common-ui/components/form/item/LabeledItem";
import { Divider } from "../../common-ui/components/layout/Divider";
import { FullWidthSpace } from "../../common-ui/components/space/FullWidthSpace";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { spacings } = DesignTokens;

const Wrapper = styled.div`
  padding: ${spacings.xlarge}px;
`;

interface ProductDetailsProps {
  data: any
}


export const ProductDetails: React.FC<ProductDetailsProps> = ({ data }) => {
  const { personalization_id } = useParams<{ personalization_id: string }>();   // personalization_id comes from URL
  const { t } = useTranslation()
  const [productInfo, setProductInfo] = useState<{
    pid_name: string | null | undefined;
    product_name: string | null;
    selected_color_image_id: string | null;
    color_name: string | null;
    view_name: string | null;
    image_url: string | null;
    design_name: string | null;
    hoop_name: string | null;
    production_rotation_degree: string | null;
    production_instructions: string | null;
    unique_colors: any[];
  }>({
    pid_name: null,
    product_name: null,
    selected_color_image_id: null,
    color_name: null,
    view_name: null,
    image_url: null,
    design_name: null,
    hoop_name: null,
    production_rotation_degree: null,
    production_instructions: null,
    unique_colors: []
  });


  useEffect(() => {
    if (!data) return;

    const selectedImageId = data.selected_color_image_id;

    // --------------------------------------------------------------------
    // 1. Find the selected color image
    // --------------------------------------------------------------------
    const img = data.product.blank.images.find(
      (item: any) => item.id === selectedImageId
    );

    // --------------------------------------------------------------------
    // 2. Find the design list entry by personalization_id
    // --------------------------------------------------------------------
    let foundDesign: any = null;

    data.product.view_list?.forEach((view: any) => {
      view.color_list?.forEach((color: any) => {
        color.design_list?.forEach((designItem: any) => {
          if (designItem.personalization_id === personalization_id) {
            foundDesign = designItem;
          }
        });
      });
    });

    // --------------------------------------------------------------------
    // 3. Extract unique colors from element_list
    // --------------------------------------------------------------------
    const uniqueColors = [];

    if (foundDesign?.design?.element_list) {
      const colorMap = new Map();

      foundDesign.design.element_list.forEach((el: any) => {
        var selColor;
        //find color by selected color
        if (el.default_color_id) {
          el.color_collection?.color_list?.forEach((c: any) => {
            if (c.id === el.default_color_id) {
              selColor = c;
            }
          });
        } else {
          selColor = el.color;
        }
        console.log(selColor);
        if (selColor) {
          const key = selColor.code || selColor.name;
          if (!colorMap.has(key)) {
            colorMap.set(key, {
              name: selColor.name,
              value: selColor.code || selColor.rgb || "",
              rgb: selColor.rgb ? selColor.rgb : selColor.color.argb.substring(2) || ""
            });
          }
        }
      });

      uniqueColors.push(...Array.from(colorMap.values()));
    }

    // --------------------------------------------------------------------
    // 4. Find the view name associated with the personalization_id
    // --------------------------------------------------------------------
    // Default to empty string
    let viewName = "";

    // Make sure view_list exists and is an array
    const viewList = data?.product?.view_list ?? [];

    // Find the view that contains the matching personalization_id
    const matchedView = viewList.find((view: any) =>
      (view?.color_list ?? []).some((color: any) =>
        (color?.design_list ?? []).some(
          (design: any) => design?.personalization_id === personalization_id
        )
      )
    );

    // If a view is found, safely assign its name
    viewName = matchedView?.name ?? "";

    // ----------------------------------------------------------
    // 5. Build final JSON model
    // --------------------------------------------------------------------
    setProductInfo({
      pid_name: personalization_id,
      product_name: data.product.name,

      selected_color_image_id: selectedImageId,
      color_name: img?.color_name || null,

      view_name: viewName || null,
      image_url: img?.url || null,

      design_name: foundDesign?.design?.name || null,
      hoop_name: foundDesign?.hoop?.name || null,
      production_rotation_degree: foundDesign?.production_rotation_degree === 0 ? t("detail.no") : t("detail.yes"),
      production_instructions: foundDesign?.production_instructions || null,
      unique_colors: uniqueColors
    });

  }, [data, personalization_id]);

  return (
    <Wrapper>

      <Row gutter={[spacings.middle, spacings.middle]}>
        <Col span={24}>
          <LabeledItem label={t("detail.label.pidName")} content={productInfo?.pid_name} size="xlarge" />
        </Col>
      </Row>
      <Divider size="middle" />
      <FullWidthSpace direction="vertical" size="small">
        <Row gutter={[spacings.middle, spacings.middle]}>
          <Col span={12}>
            <LabeledItem label={t("detail.label.product")} content={productInfo?.product_name} size="xlarge" />
          </Col>
          <Col span={12}>
            <LabeledItem label={t("detail.label.productColor")} content={productInfo?.color_name} size="xlarge" />
          </Col>
        </Row>
        <Row gutter={[spacings.middle, spacings.middle]}>
          <Col span={12}>
            <LabeledItem label={t("detail.label.view")} content={productInfo?.view_name} size="xlarge" />
          </Col>
          <Col span={12}>
            <LabeledItem
              label={t("detail.label.design")}
              content={productInfo?.design_name}
              size="xlarge"
            />
          </Col>
        </Row>
        <Row gutter={[spacings.middle, spacings.middle]}>
          <Col span={12}>
            <LabeledItem
              label={t("detail.label.hoop")}
              content={productInfo?.hoop_name}
              size="xlarge"
            />
          </Col>
          <Col span={12}>
            <LabeledItem
              label={t("detail.label.rotate180ForProduction")}
              content={productInfo?.production_rotation_degree}
              size="xlarge"
            />
          </Col>
        </Row>
      </FullWidthSpace>
      <Divider size="middle" />
      <Row gutter={[spacings.middle, spacings.middle]}>
        <Col span={24}>
          <LabeledItem
            label={t("detail.label.uniqueColors")}
            content={<UniqueColors colors={productInfo?.unique_colors} />}
            size="xlarge"
          />
        </Col>
      </Row>
      <Divider size="middle" />
      <Row gutter={[spacings.middle, spacings.middle]}>
        <Col span={24}>
          <LabeledItem
            label={t("detail.label.productionInstructions")}
            content={productInfo?.production_instructions}
            size="xlarge"
          />
        </Col>
      </Row>
    </Wrapper>
  );
}
